import { LitElement, html } from 'lit';
import { property, query, state } from 'lit/decorators.js';
import { styles } from '../view/styles';
import { GzDynamicFormInputWebComponent } from './GzDynamicFormInput';
import { FormGroup } from '../../gzForm/formGroup/form-group';
import { GzButtonWebComponent } from '../../gzButton';
import { WEB_COMPONENT_GZ_DYNAMIC_FORM_INPUT } from '../../../webcomponents.constants';
import { Subscription } from 'rxjs';
import { FormBuildConfig } from '../../gzForm/formGroup/types/form-build-config';
import { ButtonColor, ButtonFill, ButtonSize, ButtonType } from '../../gzIconButton/types';
import { FormValidator } from '../../gzForm/formField/types/form-validator';
import { nanoid } from 'nanoid';

export class GzDynamicFormWrapperWebComponent extends LitElement {
  // see https://lit-element.polymer-project.org/guide/properties
  @property({ type: Array, reflect: true, attribute: true })
  private dynamicInputsArray: Array<string> = ['subscription-id-0'];

  @property({ type: String, reflect: true })
  label = '';

  @state()
  inputValidators: FormValidator<string>[];

  @query('#add-new')
  submitBtn: GzButtonWebComponent;

  @state()
  public formGroup: FormGroup;

  @state()
  private subscription: Subscription;

  private get dynamicInputs() {
    return this.shadowRoot?.querySelectorAll(WEB_COMPONENT_GZ_DYNAMIC_FORM_INPUT);
  }

  // see https://lit-element.polymer-project.org/guide/styles
  static get styles() {
    return styles;
  }

  render() {
    return html`
      <div class="dynamic-form-wrapper">
        <gz-text>${this.label}</gz-text>
        ${this.renderDynamicForm()}
        <gz-icon-button
          fontSize="20"
          size="${ButtonSize.SIZE_32}"
          buttonFill="${ButtonFill.PRIMARY}"
          icon="ds-icon-add"
          buttonType="${ButtonType.SQUARE}"
          buttonColor="${ButtonColor.WHITE}"
          @click="${this.addNewDynamicInput}"
          id="add-new"
        >
        </gz-icon-button>
      </div>
    `;
  }

  private buildFormGroup() {
    if (!this.dynamicInputs) {
      return;
    }

    this.dynamicInputs.forEach((field: GzDynamicFormInputWebComponent) => {
      if (!field.formField) {
        return;
      }

      field.formField.Validators = this.inputValidators;
    });

    const config: FormBuildConfig = Array.from(this.dynamicInputs).reduce(
      (obj, item: GzDynamicFormInputWebComponent, index) => {
        const id = item.getAttribute('id');
        if (id) {
          obj[id] = item.formField;
        }
        return obj;
      },
      {}
    );

    this.formGroup = FormGroup.build(config);

    this.subscription = this.formGroup.valid$.subscribe({
      next: valid => {
        this.submitBtn.disabled = !valid;
      },
    });
  }

  private addNewDynamicInput() {
    const index = nanoid(5);
    const newItem = 'subscription-id-' + index;
    this.dynamicInputsArray = [...this.dynamicInputsArray, newItem];
  }

  private handleChildCreated(e) {
    const newItem = e.detail.itemId;
    const newCreatedInput = this.shadowRoot?.querySelector(`#${newItem}`) as GzDynamicFormInputWebComponent;

    this.formGroup?.addFormChild([`${newItem}`], newCreatedInput.formField);
    this.buildFormGroup();
  }

  private renderDynamicFormItem(label: string, index: number) {
    const isClearable = this.dynamicInputsArray.length > 1;
    const value = this.formGroup?.getFormChild([label])?.value;
    return html`<gz-dynamic-form-input
      @delete="${this.deleteDynamicInput}"
      @created="${this.handleChildCreated}"
      id="${label}"
      .value=${value}
      ?isClearable="${isClearable}"
    ></gz-dynamic-form-input>`;
  }

  private renderDynamicForm() {
    return this.dynamicInputsArray.map((option, index) => this.renderDynamicFormItem(option, index));
  }

  private deleteDynamicInput(e: CustomEvent) {
    const id = e.detail.itemId;
    const itemIndexInArray = this.dynamicInputsArray.indexOf(id);

    this.formGroup.removeFormChild([`${id}`]);

    this.dynamicInputsArray = this.dynamicInputsArray.filter((_, i) => i !== itemIndexInArray);

    this.updateComplete.then(() => {
      this.buildFormGroup();
    });
  }
}
